/**
 * Main Layout Component - NiceAdmin-style shell.
 * Edge-attached sidebar + flat top AppBar + content canvas with soft surface.
 */

import React, { useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Sidebar, { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './Sidebar';
import Header from './Header';
import { useThemeMode, ForceLightTheme } from '../../context/ThemeContext';
import VerifyEmailBanner from '../VerifyEmailBanner';
import UploadStalenessBanner from '../UploadStalenessBanner';

// The authed app is light-only and shares the public site's design tokens
// (owner decision 2026-08-10); ForceLightTheme pins the palette exactly like
// PublicLayout does.
function LayoutInner({ children }) {
  const theme = useTheme();
  const { palette } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleDrawerToggle = () => { setSidebarOpen(!sidebarOpen); };
  const toggleCollapse = () => { setCollapsed(!collapsed); };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: palette.surface }}>
      <Sidebar
        open={sidebarOpen}
        onClose={handleDrawerToggle}
        isMobile={isMobile}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapse}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Flex items default to min-width:auto, so one wide table would
          // stretch the whole page sideways on mobile; minWidth 0 keeps the
          // page fixed and lets each table/carousel scroll inside itself.
          minWidth: 0,
          width: { md: `calc(100% - ${collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.25s ease',
        }}
      >
        <Header
          isMobile={isMobile}
          onMenuClick={handleDrawerToggle}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
        />

        {/* Page Content - top margin clears the fixed AppBar */}
        <Box
          sx={{
            flexGrow: 1,
            px: { xs: 2, sm: 3, md: 4 },
            pt: { xs: 2.5, sm: 3.5 },
            pb: { xs: 3, sm: 4 },
            mt: { xs: 8, md: 9 },
          }}
        >
          <VerifyEmailBanner />
          <UploadStalenessBanner />
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function Layout({ children }) {
  return (
    <ForceLightTheme>
      <LayoutInner>{children}</LayoutInner>
    </ForceLightTheme>
  );
}

export default Layout;
